"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { FiBookOpen, FiFileText, FiSettings } from "react-icons/fi";
import type { ChatSourceBundle, SettingsSource } from "@/components/ai-chat/types";
import { ProgressBarLink } from "@/components/progress-bar";
import { getWikiArticle } from "@/api/wiki";
const PILL_CLASS = "rounded-md bg-background-secondary px-2 py-0.5 text-caption text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground";
function docPathToTitle(path: string): string {
  const withoutExt = path.replace(/\.mdx$/, "");
  const segments = withoutExt.split("/");
  let last = segments[segments.length - 1];
  if (last === "index" && segments.length > 1) {
    last = segments[segments.length - 2];
  }
  return last.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
function WikiLinks(t0) {
  const $ = _c(7);
  const {
    articleIds
  } = t0;
  let t1;
  if ($[0] !== articleIds) {
    t1 = {
      queryKey: ["wiki-sources", ...articleIds],
      queryFn: async () => {
        const results = await Promise.allSettled(articleIds.map(_temp));
        return results.filter(_temp2).map(_temp3);
      },
      staleTime: 300000
    };
    $[0] = articleIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: articles
  } = useQuery(t1);
  if (!articles || articles.length === 0) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiBookOpen className="h-3 w-3 shrink-0 text-foreground-muted" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== articles) {
    t3 = articles.map(_temp4);
    $[3] = articles;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="mt-1 flex flex-wrap items-center gap-1.5">{t2}{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp4(article) {
  return <ProgressBarLink key={article.id} href={`/wiki/${article.id}`} className={PILL_CLASS}>{article.title}</ProgressBarLink>;
}
function _temp3(r_0) {
  return r_0.value;
}
function _temp2(r) {
  return r.status === "fulfilled";
}
function _temp(id) {
  return getWikiArticle(id);
}
function DocLinks(t0) {
  const $ = _c(5);
  const {
    paths
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FiFileText className="h-3 w-3 shrink-0 text-foreground-muted" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== paths) {
    t2 = paths.map(_temp5);
    $[1] = paths;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="mt-1 flex flex-wrap items-center gap-1.5">{t1}{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp5(path) {
  return <a key={path} href={`https://docs.decodahealth.com/${path.replace(/\.mdx$/, "")}`} target="_blank" rel="noopener noreferrer" className={PILL_CLASS}>{docPathToTitle(path)}</a>;
}
function SettingsLinks(t0) {
  const $ = _c(5);
  const {
    sources
  } = t0;
  let t1;
  if ($[0] !== sources) {
    t1 = Array.from(new Map(sources.map(_temp6)).values());
    $[0] = sources;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const uniqueSources = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiSettings className="h-3 w-3 shrink-0 text-foreground-muted" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== uniqueSources) {
    t3 = <div className="mt-1 flex flex-wrap items-center gap-1.5">{t2}{uniqueSources.map(_temp7)}</div>;
    $[3] = uniqueSources;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp7(source_0) {
  return source_0.route ? <ProgressBarLink key={source_0.sourceId} href={source_0.route} className={PILL_CLASS}>{source_0.label}</ProgressBarLink> : <span key={source_0.sourceId} className={PILL_CLASS}>{source_0.label}</span>;
}
function _temp6(source) {
  return [source.sourceId, source];
}
interface SourceLinksProps {
  sources: ChatSourceBundle;
}
export function SourceLinks(t0) {
  const $ = _c(13);
  const {
    sources
  } = t0;
  const hasWiki = sources.wikiArticleIds && sources.wikiArticleIds.length > 0;
  const hasDocs = sources.docPaths && sources.docPaths.length > 0;
  const hasSettings = sources.settingsSources && sources.settingsSources.length > 0;
  if (!hasWiki && !hasDocs && !hasSettings) {
    return null;
  }
  let t1;
  if ($[0] !== hasWiki || $[1] !== sources.wikiArticleIds) {
    t1 = hasWiki && <WikiLinks articleIds={sources.wikiArticleIds} />;
    $[0] = hasWiki;
    $[1] = sources.wikiArticleIds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hasDocs || $[4] !== sources.docPaths) {
    t2 = hasDocs && <DocLinks paths={sources.docPaths} />;
    $[3] = hasDocs;
    $[4] = sources.docPaths;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== hasSettings || $[7] !== sources.settingsSources) {
    t3 = hasSettings && <SettingsLinks sources={sources.settingsSources} />;
    $[6] = hasSettings;
    $[7] = sources.settingsSources;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}